import { Collapsible, CollapsibleBoundary, CollapsibleTrigger } from "@suse-o11y/collapsible";
import { px } from "@suse-o11y/px";
import { useOnResize } from "@suse-o11y/use-on-resize";
import React from "react";

export interface TruncateMultilineProps {
	className?: string;
	lineClamp: number;
	children: React.ReactNode;
}

export const TruncateMultiline: React.FC<TruncateMultilineProps> = ({ className, lineClamp, children }) => {
	const rootRef = React.useRef<HTMLDivElement>(null);
	const [content, setContent] = React.useState<HTMLDivElement | null>(null);
	const [initialHeight, setInitialHeight] = React.useState<number>();
	const [{ scrollHeight, clientHeight }, setMeasurements] = React.useState({ scrollHeight: 0, clientHeight: 0 });

	const expanded = initialHeight != null;
	const measure = React.useCallback(() => {
		if (content == null) {
			return;
		}

		const next = { scrollHeight: content.scrollHeight, clientHeight: content.clientHeight };
		setMeasurements((previous) =>
			previous.scrollHeight === next.scrollHeight && previous.clientHeight === next.clientHeight ? previous : next,
		);
	}, [content]);

	useOnResize(measure, { node: content });
	// biome-ignore lint/correctness/useExhaustiveDependencies: These props change the committed DOM even when its observed size stays the same.
	React.useLayoutEffect(measure, [measure, children, lineClamp, expanded]);

	return (
		<CollapsibleBoundary
			className={className}
			onOpenChange={(open) => {
				setInitialHeight(open ? rootRef.current?.clientHeight : undefined);
			}}
			open={expanded}
			ref={rootRef}
		>
			<Collapsible
				className="collapsible-state-closed:line-clamp-(--line-clamp-count) max-h-in-from-(--initial-content-height) max-h-in-to-content-height overflow-hidden duration-200 motion-safe:collapsible-state-open:animate-in"
				forceMount
				ref={setContent}
				style={
					{
						"--line-clamp-count": lineClamp,
						"--content-height": scrollHeight > 0 ? px(scrollHeight) : undefined,
						"--initial-content-height": initialHeight == null ? undefined : px(initialHeight),
					} as React.CSSProperties
				}
			>
				{children}
			</Collapsible>

			{!expanded && scrollHeight > clientHeight && (
				<CollapsibleTrigger className="link link-secondary">Read more</CollapsibleTrigger>
			)}
		</CollapsibleBoundary>
	);
};
